import type { FormConfig } from '../form-engine.js';
import { VERTICAL_KEYS } from '../vertical.js';

// =============================================================================
// Imobiliário geral FormConfig · compra pra morar ou investir, sem a régua
// de renda do MCMV. Primeiro tenant: Angélica Coelho, corretora (Grande Floripa).
//
// Só comprador/investidor: quem quer vender o próprio imóvel raramente vem
// do anúncio e teria outro caminho de perguntas.
//
// As faixas (valor, renda) são enums em vez de número livre · o lead
// responde mais rápido e o qualifier compara faixa com faixa. Os valores
// dos enums são lidos pelo qualifier em apps/api/src/verticals/imobiliario-geral.
//
// Copy impessoal ("você recebe o retorno") porque o tenant pode ser uma
// imobiliária com equipe ou um corretor sozinho.
// =============================================================================

export const imobiliarioGeralFormConfig: FormConfig = {
  version: 1,
  verticalKey: VERTICAL_KEYS.IMOBILIARIO_GERAL,
  submitLabel: 'Encontrar meu imóvel',
  steps: [
    {
      id: 'contact',
      title: 'Pra começar, seu contato',
      subtitle: 'No final, o WhatsApp abre com a sua mensagem pronta.',
      fields: [
        {
          id: 'name',
          scope: 'lead',
          type: 'text',
          label: 'Nome completo',
          placeholder: 'João Silva',
          validation: { required: true, minLength: 2, maxLength: 160 },
        },
        {
          id: 'email',
          scope: 'lead',
          type: 'email',
          label: 'E-mail',
          placeholder: 'joao@email.com',
          validation: { required: true },
        },
        {
          id: 'phone',
          scope: 'lead',
          type: 'phone',
          label: 'WhatsApp (com DDD)',
          placeholder: '(48) 99999-9999',
          validation: { required: true },
        },
      ],
    },
    {
      id: 'property',
      title: 'O imóvel que você procura',
      subtitle: 'Pra separar as opções certas pra você.',
      fields: [
        {
          id: 'purpose',
          type: 'single-choice',
          label: 'Você quer o imóvel pra...',
          columns: 2,
          validation: { required: true },
          options: [
            { value: 'live', label: 'Morar', iconName: 'Home' },
            { value: 'invest', label: 'Investir', iconName: 'PiggyBank' },
          ],
        },
        {
          id: 'propertyType',
          type: 'single-choice',
          label: 'Que tipo de imóvel?',
          columns: 2,
          validation: { required: true },
          options: [
            { value: 'apartment', label: 'Apartamento', iconName: 'Building2' },
            { value: 'house', label: 'Casa', iconName: 'Home' },
            { value: 'land', label: 'Terreno', iconName: 'MapPin' },
            { value: 'commercial', label: 'Sala ou loja', iconName: 'Briefcase' },
          ],
        },
        {
          id: 'bedrooms',
          type: 'single-choice',
          label: 'Quantos quartos?',
          columns: 2,
          condition: { field: 'propertyType', in: ['apartment', 'house'] },
          validation: { required: true },
          options: [
            { value: '1', label: '1 quarto' },
            { value: '2', label: '2 quartos' },
            { value: '3', label: '3 quartos' },
            { value: '4_plus', label: '4 ou mais' },
          ],
        },
        {
          id: 'region',
          type: 'single-choice',
          label: 'Em qual cidade?',
          columns: 2,
          validation: { required: true },
          options: [
            { value: 'florianopolis', label: 'Florianópolis' },
            { value: 'sao_jose', label: 'São José' },
            { value: 'palhoca', label: 'Palhoça' },
            { value: 'biguacu', label: 'Biguaçu' },
            { value: 'other', label: 'Outra cidade' },
          ],
        },
        {
          id: 'regionOther',
          type: 'text',
          label: 'Qual cidade?',
          placeholder: 'Cidade · Estado',
          condition: { field: 'region', equals: 'other' },
          validation: { required: true, minLength: 2, maxLength: 80 },
        },
        {
          id: 'timeline',
          type: 'single-choice',
          label: 'Quando pretende comprar?',
          validation: { required: true },
          options: [
            { value: 'up_to_3_months', label: 'Nos próximos 3 meses', iconName: 'CalendarCheck2' },
            { value: '3_to_6_months', label: 'De 3 a 6 meses', iconName: 'CalendarClock' },
            { value: '6_to_12_months', label: 'De 6 meses a 1 ano', iconName: 'CalendarRange' },
            { value: 'just_browsing', label: 'Só pesquisando por enquanto', iconName: 'CalendarDays' },
          ],
        },
      ],
    },
    {
      id: 'budget',
      title: 'Valor e forma de pagamento',
      subtitle: 'Pra entender o que cabe no seu planejamento.',
      fields: [
        {
          id: 'priceRange',
          type: 'single-choice',
          label: 'Até quanto você pretende investir no imóvel?',
          validation: { required: true },
          options: [
            { value: 'up_to_350k', label: 'Até R$ 350 mil' },
            { value: '350k_600k', label: 'De R$ 350 mil a R$ 600 mil' },
            { value: '600k_1m', label: 'De R$ 600 mil a R$ 1 milhão' },
            { value: 'above_1m', label: 'Acima de R$ 1 milhão' },
          ],
        },
        {
          id: 'paymentMethod',
          type: 'single-choice',
          label: 'Como pretende pagar?',
          columns: 2,
          validation: { required: true },
          options: [
            { value: 'financing', label: 'Financiamento', iconName: 'Key' },
            { value: 'financing_fgts', label: 'Financiamento + FGTS', iconName: 'Wallet' },
            { value: 'cash', label: 'À vista', iconName: 'Coins' },
            { value: 'exchange', label: 'Imóvel na troca', iconName: 'Handshake' },
          ],
        },
        {
          id: 'monthlyIncome',
          type: 'single-choice',
          label: 'Qual a renda familiar mensal?',
          hint: 'Pode somar a renda de quem vai comprar junto com você.',
          condition: { field: 'paymentMethod', in: ['financing', 'financing_fgts'] },
          validation: { required: true },
          options: [
            { value: 'up_to_5k', label: 'Até R$ 5 mil' },
            { value: '5k_10k', label: 'De R$ 5 mil a R$ 10 mil' },
            { value: '10k_15k', label: 'De R$ 10 mil a R$ 15 mil' },
            { value: '15k_25k', label: 'De R$ 15 mil a R$ 25 mil' },
            { value: 'above_25k', label: 'Acima de R$ 25 mil' },
          ],
        },
      ],
    },
    {
      // Etapa própria pra entrada: no celular, valor + pagamento + renda +
      // carro + entrada numa tela só passavam de 20 opções pra rolar.
      id: 'downpayment',
      title: 'Sua entrada',
      subtitle: 'Último passo: o que você já tem pra começar.',
      fields: [
        {
          id: 'hasCarForDownPayment',
          type: 'yes-no',
          label: 'Tem um carro pra dar como parte da entrada?',
          hint: 'O carro pode entrar no pagamento.',
          validation: { required: true },
        },
        {
          id: 'carModel',
          type: 'text',
          label: 'Qual carro? (modelo e ano)',
          placeholder: 'Ex: Onix 2020',
          condition: { field: 'hasCarForDownPayment', equals: true },
          validation: { required: true, minLength: 2, maxLength: 80 },
        },
        {
          id: 'downPayment',
          type: 'single-choice',
          label: 'Quanto você tem pra entrada?',
          hint: 'Some dinheiro guardado, FGTS e o valor do carro, se for usar.',
          condition: { field: 'paymentMethod', notEquals: 'cash' },
          validation: { required: true },
          options: [
            { value: 'up_to_20k', label: 'Até R$ 20 mil' },
            { value: '20k_40k', label: 'De R$ 20 mil a R$ 40 mil' },
            { value: '40k_80k', label: 'De R$ 40 mil a R$ 80 mil' },
            { value: 'above_80k', label: 'Acima de R$ 80 mil' },
          ],
        },
      ],
    },
  ],
};
